(ns manage.ad-variant-insights.charts
  (:require ["@mui/material/Typography$default" :as Typography]
            ["@mui/material/styles" :as styles]
            ["@mui/x-charts" :refer [axisClasses BarChart LineChart]]
            ["@mui/x-charts-vendor/d3-interpolate" :refer [interpolateObject]]
            ["@mui/x-charts/hooks" :refer [useAnimate]]
            [cljs-bean.core :refer [->js]]
            [common.util :as util]
            [goog.object :as gobject]
            [uix.core :as uix :refer [$ defui]]))

(defn style-fn [^js theme]
  (let [left-axis-label (str "." (.-left axisClasses)
                             " ." (.-label axisClasses))
        right-axis-label (str "." (.-right axisClasses)
                              " ." (.-label axisClasses))
        bottom-axis-label (str "." (.-bottom axisClasses)
                               " ." (.-label axisClasses))
        line-axis-class (str "." (.-line axisClasses))
        left-tick-label (str "." (.-left axisClasses)
                             " ." (.-tickLabel axisClasses))
        bottom-tick-label (str "." (.-bottom axisClasses)
                               " ." (.-tickLabel axisClasses))]
    (->js {:pt 3
           left-axis-label {"text" {:fontSize "12px !important"}}
           right-axis-label {"text" {:fontSize "12px !important"}}
           bottom-axis-label {"text" {:fontSize "12px !important"}}
           ".MuiMarkElement-root" {"r" 4
                                   "strokeWidth" 2}
           "& .MuiChartsGrid-line" {:stroke "grey.20"
                                    :strokeWidth 1}
           left-tick-label {:transform "translateX(-5px)"}
           bottom-tick-label {:transform "translateY(5px)"}
           line-axis-class {:stroke (str (gobject/get (.. theme -palette -grey) "70") " !important")}}
          :recursive true)))

(defn format-date-mm-dd [date-str]
  (when date-str
    (let [date (js/Date. date-str)
          month (inc (.getMonth date))
          day (.getDate date)]
      (str (when (< month 10) "0") month "/"
           (when (< day 10) "0") day))))

(defn format-ctr-decimal [v]
  (when v
    (.toFixed v 2)))

(defui ctr-performance-trend-chart
  [{:keys [time-series optimized?]}]
  (let [theme (styles/useTheme)
        positive-color (.. theme -palette -data -positive)
        warning-color (.. theme -palette -data -warning)

        dataset (uix/use-memo
                 (fn []
                   (->js (mapv (fn [{:keys [date ctr optimized-ctr control-ctr]}]
                                 {:label (format-date-mm-dd date)
                                  :ctr ctr
                                  :optimized optimized-ctr
                                  :control control-ctr})
                               time-series)
                         :recursive true))
                 [time-series])]
    ($ LineChart
       {:dataset dataset
        :margin #js {:top 0
                     :bottom 24
                     :right 0
                     :left 0}
        :slotProps
        #js {:legend
             #js {:position #js {:vertical "bottom"
                                 :horizontal "center"}
                  :sx #js {".MuiChartsLabelMark-fill" #js {"r" 6}}}}

        :height 320
        :grid #js {:vertical false
                   :horizontal true}
        :xAxis #js [#js {:scaleType "band"
                         :dataKey "label"
                         :tickSize 0
                         :id "time"}]
        :yAxis #js [#js {:id "ctr"
                         :label "CTR"
                         :sx #js {"background" "green"}
                         :tickSize 0
                         :valueFormatter format-ctr-decimal}]
        :& [(when optimized?
              {:series #js [#js {:type "line"
                                 :label "Optimized"
                                 :dataKey "optimized"
                                 :yAxisId "ctr"
                                 :showMark true
                                 :labelMarkType "circle"
                                 :color positive-color
                                 :valueFormatter format-ctr-decimal}
                            #js {:type "line"
                                 :label "Control"
                                 :dataKey "control"
                                 :yAxisId "ctr"
                                 :showMark true
                                 :labelMarkType "circle"
                                 :color warning-color
                                 :valueFormatter format-ctr-decimal}]})
            (when-not optimized?
              {:series #js [#js {:type "line"
                                 :label "CTR"
                                 :dataKey "ctr"
                                 :yAxisId "ctr"
                                 :showMark true
                                 :labelMarkType "circle"
                                 :color positive-color
                                 :valueFormatter format-ctr-decimal}]})]
        :sx style-fn})))

(def BarLabel
  (uix/as-react
   (fn [{:keys [yOrigin x y width skipAnimation]
         :as props}]
     (let [animated-props (useAnimate
                           #js {:x (+ (or x 0) (/ (or width 0) 2))
                                :y (- y 18)}
                           #js {:initialProps #js {:x (+ x (/ width 2))
                                                   :y yOrigin}
                                :createInterpolator interpolateObject
                                :transformProps identity
                                :applyProps (fn [element p]
                                              (.setAttribute element "x" (str (.-x p)))
                                              (.setAttribute element "y" (str (.-y p))))
                                :skip skipAnimation})]
       ($ Typography
          (merge (dissoc props :seriesId :dataIndex :color :isFaded :isHighlighted
                         :classes :xOrigin :yOrigin :x :y :width :height
                         :layout :skipAnimation)
                 {:component "text"
                  :variant "body2"
                  :fill "grey.100"
                  :stroke "none"
                  :textAnchor "middle"
                  :dominantBaseline "central"
                  :sx #js {:pointerEvents "none"
                           :transition "opacity 0.2s ease-in, fill 0.2s ease-in"}}
                 (js->clj animated-props :keywordize-keys true)))))))

(defui ctr-comparison-chart
  [{:keys [ctr control-ctr optimized-ctr optimized?]}]
  (let [theme (styles/useTheme)
        warning-color (.. theme -palette -data -warning)
        positive-color (.. theme -palette -data -positive)

        dataset (uix/use-memo
                 (fn []
                   (if optimized?
                     #js [#js {:category "Control"
                               :value control-ctr}
                          #js {:category "Optimized"
                               :value optimized-ctr}]
                     #js [#js {:category "ctr"
                               :value ctr}]))
                 [optimized? ctr control-ctr optimized-ctr])]

    ($ BarChart
       {:dataset dataset
        :height 305
        :margin 0
        :slots #js {:barLabel BarLabel}
        :grid #js {:vertical false
                   :horizontal true}
        :borderRadius 4
        :barLabel (fn [^js item _]
                    (util/->locale-string (.-value item)
                                          {:style "percent"
                                           :maximumFractionDigits 2}))
        :xAxis #js [#js {:scaleType "band"
                         :tickSize 0
                         :categoryGapRatio 0.625
                         :colorMap #js {:type "ordinal"
                                        :colors #js [warning-color positive-color]}
                         :dataKey "category"}]
        :yAxis #js [#js {:id "ctr"
                         :label "CTR"
                         :tickSize 0
                         :domainLimit (fn [min max]
                                        #js {:min min
                                             :max (+ 0.5 (* 0.5 (js/Math.ceil (/ max 0.5))))})
                         :valueFormatter format-ctr-decimal}]
        :series #js [#js {:dataKey "value"
                          :valueFormatter format-ctr-decimal}]
        :sx style-fn})))
